import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import IconButton from '@mui/material/IconButton';
import MuiLink from '@mui/material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

import StaticAdsService from 'services/StaticAdsService';
import { aspectLabel, hasPlacement, STATIC_ASPECTS } from './staticAspects';
import InspoSetsPanel from './InspoSetsPanel';

import { bumpStaticActivity, onStaticActivityBump } from './activityBus';
import formatEnumLabel from './formatEnumLabel';
import useStaticFormatLabels from './useStaticFormatLabels';
import { groupOriginLine, isStructured, originLine } from './generationFramework';
import OverlayEditor from './OverlayEditor';
import AdCopyPanel from './AdCopyPanel';
import FinalizeDialog from './FinalizeDialog';
import CreativeNameField from './CreativeNameField';
import GroupDetailDialog from './GroupDetailDialog';
import TagEditor from 'shared/components/TagEditor';
import ProductAssetPicker from './ProductAssetPicker';
import StaticAdDetailDialog from './StaticAdDetailDialog';
import { shouldPoll } from 'shared/pollingGate';
import { DisplayUnit, foldRows, groupStatus } from './staticGroups';
import { SIMILARITY_LABEL, SIMILARITY_TIP, SOURCE_ATTACHED_AXES, rowSourceAttached } from './similarityAxis';

import { GenerationRow, GenerationStatus, GroupInfo, ProductAsset, ProductSummary, StaticDelivery } from './types';

// Generated creative — what came out of the pipeline, next to what went in.
//
// Mirrors the Library's card/table switch. Every row shows the source ad it was
// rebuilt from and the product it was rebuilt around, because "which ad, which
// product" is the first question about any output. The exact prompt sent is one
// click away in the detail dialog — it is the reproducible artifact.

const VIEW_KEY = 'sbly.staticOutput.view';
const POLL_INTERVAL_MS = 8000;

const STATUS_COLOR: Record<GenerationStatus, 'default' | 'info' | 'success' | 'error' | 'warning'> = {
  PENDING: 'warning',
  RUNNING: 'info',
  DONE: 'success',
  FAILED: 'error',
};

// Saved launch copy at a glance: a DONE output without headlines/captions
// still needs the Ad Copy Studio pass before it can ship. Takes the raw
// object-or-JSON-string value so a group's shared copy answers the same
// question the same way (AdCopyPanel.parseAdCopy parses it identically).
function hasCopy(raw: GenerationRow['adCopy']): boolean {
  const value = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
  const some = (arr: unknown) => Array.isArray(arr) && arr.some(v => typeof v === 'string' && v.trim());
  return some(value?.headlines) || some(value?.captions);
}

const STATUS_LABEL: Record<GenerationStatus, string> = {
  PENDING: 'Queued',
  RUNNING: 'Generating',
  DONE: 'Done',
  FAILED: 'Failed',
};

const PAGE_SIZE = 24;

export default function GenerationOutput() {
  const service = useMemo(() => new StaticAdsService(), []);
  const [rows, setRows] = useState<GenerationRow[]>([]);
  // Group metadata keyed by groupId, straight off the list response — the rows
  // only carry the id, so member count and shared copy live here.
  const [groups, setGroups] = useState<Record<string, GroupInfo>>({});
  // Every group any load has SEEN, merged across pages (the current page wins).
  // `groups` is replaced wholesale per page while `selectedRows` persists, so
  // finalizing a group selected on another page must seed its stored name from
  // here; display still reads `groups`.
  const [knownGroups, setKnownGroups] = useState<Record<string, GroupInfo>>({});
  // Which copy this row would actually LAUNCH with. While a row is grouped the
  // group's shared copy is what publishStatic writes and the row's own ad_copy
  // is dormant, so asking the row alone told the operator a creative was ready
  // when it was about to publish with no copy at all.
  const rowHasCopy = (row: GenerationRow): boolean => {
    const info = row.groupId ? groups[row.groupId] : undefined;
    return info ? hasCopy(info.adCopy) : hasCopy(row.adCopy);
  };
  const copyTip = (row: GenerationRow): string => {
    const grouped = !!(row.groupId && groups[row.groupId]);
    if (rowHasCopy(row)) {
      return grouped
        ? "Publishes with its group's shared headlines/captions"
        : 'Headlines/captions are saved on this output';
    }
    return grouped
      ? "Its group has no shared headlines or captions yet — this would publish with no copy. Open the group and use the Ad Copy Studio."
      : 'No headlines or captions saved yet — open it and use the Ad Copy Studio';
  };
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'grid' | 'table'>(() => (
    (localStorage.getItem(VIEW_KEY) as 'grid' | 'table') || 'grid'
  ));
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [productFilter, setProductFilter] = useState('');
  // 'active' hides FAILED — the default, so the grid shows work rather than
  // wreckage; failures stay one filter click away with their reasons intact.
  const [statusFilter, setStatusFilter] = useState<'active' | 'all' | 'FAILED' | 'DONE'>('active');
  // useState, not a literal — a fresh array in load's deps would re-fire it on
  // every render.
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [openRow, setOpenRow] = useState<GenerationRow | null>(null);
  // The opened group. Held as the folded unit, not just an id, so the dialog
  // renders straight from it; an effect below re-derives it after every load.
  const [openGroup, setOpenGroup] = useState<Extract<DisplayUnit, { kind: 'group' }> | null>(null);
  // Multi-select for finalize — DONE rows only. A Map of full rows, not ids:
  // the list is paged and filtered, so an id-only selection intersected with
  // the visible page silently became "Finalize 0 creatives" after any page or
  // filter change. Captured rows survive both.
  const [selectedRows, setSelectedRows] = useState<Map<string, GenerationRow>>(new Map());
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const toggleSelected = (row: GenerationRow) => setSelectedRows(prev => {
    const next = new Map(prev);
    if (next.has(row.generationId)) next.delete(row.generationId);
    else next.set(row.generationId, row);
    return next;
  });
  // A group's checkbox is all-or-nothing over its DONE members: only DONE
  // outputs can be finalized, so ticking a half-generated group selects the
  // ones that are actually publishable rather than silently doing nothing.
  const toggleGroupSelected = (members: GenerationRow[]) => setSelectedRows(prev => {
    const done = members.filter(r => r.status === 'DONE');
    if (!done.length) return prev;
    const allSelected = done.every(r => prev.has(r.generationId));
    const next = new Map(prev);
    for (const row of done) {
      if (allSelected) next.delete(row.generationId);
      else next.set(row.generationId, row);
    }
    return next;
  });

  const [loadError, setLoadError] = useState(false);
  // Requests can overlap (a poll-triggered reload while a filter change is in
  // flight); only the NEWEST may write state, or a slow stale response
  // overwrites the page the user is actually looking at. Same guard as the
  // Library's load.
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const resp = await service.listGenerations(
        {
          productId: productFilter || undefined,
          status: statusFilter === 'FAILED' || statusFilter === 'DONE' ? statusFilter : undefined,
          excludeFailed: statusFilter === 'active' ? '1' : undefined,
          tag: tagFilter.length ? tagFilter : undefined,
        },
        { limit: PAGE_SIZE, offset: page * PAGE_SIZE },
      );
      if (seq !== loadSeq.current) return;
      if (!resp) {
        // A failed list must not render as "Nothing generated yet".
        setLoadError(true);
        return;
      }
      setLoadError(false);
      const fresh = resp.generations || [];
      setRows(fresh);
      setGroups(resp.groups || {});
      setKnownGroups(prev => ({ ...prev, ...(resp.groups || {}) }));
      // `total` counts CARDS server-side (a group is one card, never split
      // across pages), so the pager agrees with the folded grid.
      setTotal(resp.total || 0);
      // Selected rows keep their captured data current when they pass through
      // the visible page; off-page selections stay as captured.
      setSelectedRows(prev => {
        if (!prev.size) return prev;
        const next = new Map(prev);
        for (const row of fresh) if (next.has(row.generationId)) next.set(row.generationId, row);
        return next;
      });
    } finally {
      // A superseded request must not clear the spinner the newest one raised.
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [service, productFilter, statusFilter, tagFilter, page]);

  useEffect(() => { void load(); }, [load]);
  // Products drive the filter dropdown; a failed load disables the control
  // rather than offering an empty list as if no products existed.
  const [productsError, setProductsError] = useState(false);
  useEffect(() => {
    void service.listProducts().then((resp) => {
      if (resp) { setProducts(resp.products || []); setProductsError(false); }
      else setProductsError(true);
    });
  }, [service]);

  // Two vocabularies from one call. The FILTER offers only what can match here
  // (creative + group tags); an ad-only tag would be an option guaranteed to
  // return nothing. The EDITORS suggest the union, where borrowing an existing
  // word is the point.
  const [filterTagOptions, setFilterTagOptions] = useState<string[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [tagsError, setTagsError] = useState(false);
  const loadTags = useCallback(async () => {
    const resp = await service.getTags();
    if (!resp) { setTagsError(true); return; }
    setTagsError(false);
    setFilterTagOptions([...new Set([...(resp.generationTags || []), ...(resp.groupTags || [])])].sort());
    setTagSuggestions(resp.suggestions || resp.tags || []);
  }, [service]);
  useEffect(() => { void loadTags(); }, [loadTags]);

  const templateLabel = useStaticFormatLabels(service);

  // Poll only while something is moving, gated the same way as the library —
  // an Output tab left open must not keep Snowflake resumed.
  const unfinished = rows.filter(r => r.status === 'PENDING' || r.status === 'RUNNING');
  const unfinishedKey = unfinished.map(r => r.generationId).join(',');
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!unfinishedKey) return undefined;
    const timer = setInterval(async () => {
      if (!shouldPoll()) return;
      const resp = await service.getGenerationStatuses(unfinishedKey.split(','));
      const statuses = resp?.statuses || [];
      const finished = statuses.some(s => s.status === 'DONE' || s.status === 'FAILED');
      if (finished) {
        // Terminal states bring new artifacts (images, error text) — reload.
        void loadRef.current();
      } else {
        // In-flight transitions (Queued → Generating) just patch the rows;
        // without this the card said "Queued" for the whole run.
        setRows(prev => prev.map((row) => {
          const fresh = statuses.find(x => x.generationId === row.generationId);
          return fresh && fresh.status !== row.status ? { ...row, status: fresh.status } : row;
        }));
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [unfinishedKey, service]);

  // Work queued from ANOTHER tab (the Library's Generate, a re-roll) lands
  // here. This tab stays mounted while hidden, so without a refetch the
  // activity chip navigated the operator to a list that neither showed the new
  // rows nor polled for them — the run looked lost. The queue insert is in
  // flight when the bump fires, so give it the same beat ActivityStrip does.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = onStaticActivityBump(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { void loadRef.current(); }, 800);
    });
    return () => { unsubscribe(); if (timer) clearTimeout(timer); };
  }, []);

  const chooseView = (next: 'grid' | 'table') => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* private mode */ }
  };

  // One error surface for every mutation, mirroring StaticAdDetailDialog's
  // run(): the backend's message is the actionable one, shown verbatim. An
  // unwrapped await left rejections as unhandled promises — the spinner
  // cleared and nothing said the action didn't happen.
  const [actionError, setActionError] = useState<string | null>(null);
  const run = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
    } catch (err: any) {
      setActionError(err?.response?.data?.error || err?.message || 'Something went wrong');
    }
  };

  // What the grid actually draws: one card per group, loose rows untouched.
  const units = useMemo(() => foldRows(rows, groups), [rows, groups]);

  // Grouping is a server-side merge — a selection that overlaps an existing
  // group is absorbed into it rather than creating a rival one — so the client
  // just posts the ids and reloads.
  //
  // The reload is the slow half, so without a busy flag the button sat unchanged
  // for the whole round trip and the click read as ignored — which invites a
  // second click, and a second POST.
  const [grouping, setGrouping] = useState(false);
  const groupSelected = () => run(async () => {
    setGrouping(true);
    try {
      await service.createGenerationGroup([...selectedRows.keys()]);
      setSelectedRows(new Map());
      await load();
    } finally {
      setGrouping(false);
    }
  });

  // Leaving a group from inside the member's own dialog: reload, then close
  // just that dialog. The group dialog underneath stays open and redraws
  // without the member — the user is mid-triage and shouldn't be thrown out
  // to the grid to get back to where they were.
  //
  // The reload is the slow half, and without a busy flag the button sat
  // unchanged for the whole round trip — the click read as ignored, which
  // invites a second one. Same treatment as `grouping` above.
  const [leavingGroup, setLeavingGroup] = useState(false);
  const removeFromGroup = (groupId: string, generationId: string) => run(async () => {
    setLeavingGroup(true);
    try {
      await service.removeGroupMember(groupId, generationId);
      await load();
      setOpenRow(null);
    } finally {
      setLeavingGroup(false);
    }
  });

  const retry = (generationId: string) => run(async () => {
    await service.retryGeneration(generationId);
    bumpStaticActivity();
    setOpenRow(null);
    await load();
  });

  // Deletion asks first (a real dialog, not window.confirm) and shows a
  // spinner veil over the creative while the delete is in flight.
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [sourceAdId, setSourceAdId] = useState<string | null>(null);
  const [confirmVersionKey, setConfirmVersionKey] = useState<string | null>(null);
  // The version being switched to or deleted — its thumbnail wears a spinner
  // and the whole strip ignores clicks until the round-trip lands.
  const [versionBusyKey, setVersionBusyKey] = useState<string | null>(null);
  // Bottom of the detail: ad copy (default) / the generation's inputs /
  // publish history with the Dropbox + Notion links.
  const [detailTab, setDetailTab] = useState<'copy' | 'inputs' | 'published'>('copy');
  const [deliveries, setDeliveries] = useState<StaticDelivery[]>([]);
  // A failed history read must not render as "Not published yet" — that
  // answer looks identical to the truthful empty case and invites
  // re-publishing something already delivered.
  const [deliveriesError, setDeliveriesError] = useState<string | null>(null);
  const openGenerationId = openRow?.generationId;
  // Opening one creative after another leaves the first read in flight; without
  // this guard its Dropbox and Notion links rendered under the SECOND creative,
  // which is an invitation to re-publish something already delivered.
  const deliverySeq = useRef(0);
  const loadDeliveries = useCallback(async (generationId: string) => {
    const seq = ++deliverySeq.current;
    setDeliveriesError(null);
    try {
      const resp = await service.listDeliveries(generationId);
      if (seq !== deliverySeq.current) return;
      setDeliveries(resp.deliveries || []);
    } catch (err: any) {
      if (seq !== deliverySeq.current) return;
      setDeliveriesError(err?.response?.data?.error || err?.message || 'Failed to load the publish history');
    }
  }, [service]);
  useEffect(() => {
    // Changing (or closing) the open creative invalidates any read in flight.
    deliverySeq.current += 1;
    setDetailTab('copy');
    setDeliveries([]);
    setDeliveriesError(null);
    if (openGenerationId) void loadDeliveries(openGenerationId);
  }, [openGenerationId, loadDeliveries]);

  const confirmDeleteVersion = async () => {
    const key = confirmVersionKey;
    if (!key || !openRow) return;
    setConfirmVersionKey(null);
    setVersionBusyKey(key);
    try {
      await run(async () => {
        await service.deleteOutputVersion(openRow.generationId, key);
        await load();
      });
    } finally {
      setVersionBusyKey(null);
    }
  };
  const [overlayEditorOpen, setOverlayEditorOpen] = useState(false);
  const [rerollOpen, setRerollOpen] = useState(false);
  const [rerollTweak, setRerollTweak] = useState('');
  const [rerollPrompt, setRerollPrompt] = useState('');
  // Product photos for the re-roll — parent's selection preloaded, editable.
  const [rerollAssets, setRerollAssets] = useState<ProductAsset[] | null>(null);
  const [rerollChosen, setRerollChosen] = useState<Set<string>>(new Set());
  const [rerollBusy, setRerollBusy] = useState(false);
  // "Add size": the same creative reframed into another aspect.
  //
  // ONE menu for the whole page, carrying the row it was opened for. There are
  // three ways in — the card's corner button, the selection toolbar and the
  // detail dialog — and a menu per entry point would be three copies of the
  // same "which ratios are missing" logic, free to disagree about what a
  // creative still needs.
  const [sizeMenu, setSizeMenu] = useState<{ anchor: HTMLElement; row: GenerationRow } | null>(null);
  // Keyed by generation id, not a single flag: the corner buttons are on the
  // grid, so several can be queued in a row and each needs its OWN spinner.
  const [sizingIds, setSizingIds] = useState<Set<string>>(new Set());

  /**
   * Which aspects this creative's group already covers, out of the rows this
   * page happens to hold.
   *
   * Best-effort by design: a sibling on another page is invisible here, so this
   * only decides what the menu OFFERS. The server owns the actual guard and
   * refuses a duplicate ratio with a message — the UI must not be the thing
   * standing between two assets and one placement.
   */
  const groupAspects = (row: GenerationRow): Set<string> => {
    const mine = rows.filter(r => (row.groupId ? r.groupId === row.groupId : r.generationId === row.generationId));
    return new Set(mine.map(r => r.aspectRatio).filter((a): a is string => !!a));
  };

  const addSize = (row: GenerationRow, aspectRatio: string) => {
    setSizeMenu(null);
    setSizingIds(prev => new Set(prev).add(row.generationId));
    return run(async () => {
      try {
        await service.resizeGeneration(row.generationId, aspectRatio);
        await load();
      } finally {
        setSizingIds(prev => {
          const next = new Set(prev);
          next.delete(row.generationId);
          return next;
        });
      }
    });
  };

  /** The ratios a creative could still be given, or [] when it has them all. */
  const missingAspects = (row: GenerationRow): string[] => {
    if (row.status !== 'DONE' || row.model === 'stub') return [];
    const have = groupAspects(row);
    return STATIC_ASPECTS.filter(a => !have.has(a));
  };

  /**
   * Why this creative wants another size, in the operator's terms.
   *
   * A creative with no story version cannot launch at all — the Ad Launcher
   * reports it as blocked — so that case says so rather than describing the
   * mechanic.
   */
  const addSizeHint = (row: GenerationRow): string => {
    const have = groupAspects(row);
    if (!hasPlacement(have, 'story')) {
      return 'No story (9:16) version yet — an ad needs one of each, so the Ad Launcher would report this as blocked.';
    }
    if (!hasPlacement(have, 'feed')) {
      return 'No feed (4:5 or 1:1) version yet — an ad needs one of each, so the Ad Launcher would report this as blocked.';
    }
    return 'Reframe this creative into another size. Same ad, new shape — they publish as one Notion row.';
  };

  // Cross-origin `download` attributes are ignored, so pull the bytes and
  // hand the browser a local object URL; fall back to a plain open if the
  // storage host refuses CORS.
  const downloadImage = async (url: string, filename: string) => {
    try {
      const resp = await fetch(url);
      if (!resp.ok) throw new Error(String(resp.status));
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      a.click();
      // Revoking synchronously races the browser actually reading the URL —
      // some browsers save an empty file. Deferred, generously.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10_000);
    } catch {
      window.open(url, '_blank', 'noopener');
    }
  };

  // The open detail follows the list: after a version switch or text-edit
  // save reloads the rows, the dialog shows the fresh row, not a stale copy.
  useEffect(() => {
    setOpenRow(prev => {
      if (!prev) return prev;
      const fresh = rows.find(r => r.generationId === prev.generationId);
      return fresh || prev;
    });
  }, [rows]);

  // Same idea for the open group: rebuild it from the reloaded rows so removing
  // a member redraws the gallery. A group that is gone from the fold — ungrouped,
  // dissolved, or filtered away entirely — closes rather than showing a stale
  // membership list the server no longer agrees with.
  useEffect(() => {
    setOpenGroup(prev => {
      if (!prev) return prev;
      const fresh = units.find(u => u.kind === 'group' && u.groupId === prev.groupId);
      return (fresh as Extract<DisplayUnit, { kind: 'group' }> | undefined) || null;
    });
  }, [units]);

  const submitReroll = async () => {
    if (!openRow) return;
    setRerollBusy(true);
    try {
      // On failure the re-roll dialog stays open with the error — its inputs
      // (prompt edits, tweak) are exactly what the user would retry with.
      await run(async () => {
        await service.rerollGeneration(openRow.generationId, {
          tweak: rerollTweak.trim() || undefined,
          prompt: rerollPrompt.trim() && rerollPrompt.trim() !== (openRow.promptText || '').trim()
            ? rerollPrompt.trim() : undefined,
          assetIds: rerollAssets ? [...rerollChosen] : undefined,
        });
        bumpStaticActivity();
        setRerollOpen(false);
        setRerollTweak('');
        setOpenRow(null);
        await load();
      });
    } finally {
      setRerollBusy(false);
    }
  };
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const askRemove = (e: React.MouseEvent | null, generationId: string) => {
    e?.stopPropagation();
    setConfirmDeleteId(generationId);
  };

  const confirmRemove = async () => {
    const generationId = confirmDeleteId;
    if (!generationId) return;
    setConfirmDeleteId(null);
    setDeletingIds(prev => new Set(prev).add(generationId));
    try {
      await run(async () => {
        await service.deleteGeneration(generationId);
        setOpenRow(prev => (prev?.generationId === generationId ? null : prev));
        // The selection holds captured ROWS so it can span pages, which means
        // nothing about a reload takes a deleted one out of it — it stayed
        // ticked and got published to Dropbox and Notion. Drop it here, where
        // the deletion is known, rather than guessing from the visible page.
        setSelectedRows(prev => {
          if (!prev.has(generationId)) return prev;
          const next = new Map(prev);
          next.delete(generationId);
          return next;
        });
        await load();
      });
    } finally {
      setDeletingIds(prev => { const next = new Set(prev); next.delete(generationId); return next; });
    }
  };

  // One loose creative — unchanged from before groups existed. Extracted only
  // so the grid can map over units and pick a renderer.
  const renderCard = (row: GenerationRow) => (
    <Card key={row.generationId} variant="outlined">
      <CardActionArea onClick={() => setOpenRow(row)}>
        <Box sx={{
          position: 'relative', aspectRatio: '1 / 1', bgcolor: 'grey.100',
          display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        }}>
          {row.outputImageUrl ? (
            <Box component="img" src={row.outputImageUrl} alt="Generated creative" loading="lazy"
              sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          ) : (
            <Stack alignItems="center" spacing={1} sx={{ color: 'text.disabled' }}>
              {(row.status === 'PENDING' || row.status === 'RUNNING') && <CircularProgress size={22} />}
              <Typography variant="caption">{STATUS_LABEL[row.status]}</Typography>
            </Stack>
          )}
          {/* The source creative, inset — output next to input is the
              whole point of this grid. */}
          {row.sourceThumbnailUrl && row.staticAdId && (
            <Tooltip title="Source ad — click to inspect the original">
              <Box component="img" src={row.sourceThumbnailUrl} alt="Source ad"
                onClick={e => { e.stopPropagation(); setSourceAdId(row.staticAdId); }}
                sx={{
                  position: 'absolute', bottom: 6, left: 6, width: 52, height: 52,
                  objectFit: 'cover', borderRadius: 1, border: '2px solid white',
                  boxShadow: 1, cursor: 'pointer', '&:hover': { transform: 'scale(1.06)' },
                  transition: 'transform 0.1s',
                }} />
            </Tooltip>
          )}
          {row.model === 'stub' && (
            <Tooltip title="Placeholder run — no Higgsfield credentials configured; this is not a real generation">
              <Chip size="small" color="warning" label="stub"
                sx={{ position: 'absolute', top: 6, right: 6, height: 20, fontSize: '0.65rem' }} />
            </Tooltip>
          )}
          {row.status === 'DONE' && (
            <Checkbox
              size="small"
              checked={selectedRows.has(row.generationId)}
              onClick={e => e.stopPropagation()}
              onChange={() => toggleSelected(row)}
              sx={{
                position: 'absolute', top: 2, left: 2, p: 0.5,
                bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
              }}
            />
          )}
          {/* Sits beside the trash so the two per-card actions live together.
              Hidden once the creative has every ratio — a button that opens an
              empty menu reads as broken. */}
          {missingAspects(row).length > 0 && (
            <Tooltip title={addSizeHint(row)}>
              <IconButton
                size="small"
                disabled={sizingIds.has(row.generationId)}
                onClick={e => { e.stopPropagation(); setSizeMenu({ anchor: e.currentTarget, row }); }}
                sx={{
                  position: 'absolute', bottom: 6, right: 42,
                  bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                }}
              >
                {sizingIds.has(row.generationId)
                  ? <CircularProgress size={16} />
                  : <AspectRatioIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            size="small"
            onClick={e => askRemove(e, row.generationId)}
            sx={{
              position: 'absolute', bottom: 6, right: 6,
              bgcolor: 'rgba(255,255,255,0.85)', '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
          {deletingIds.has(row.generationId) && (
            <Box sx={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', bgcolor: 'rgba(255,255,255,0.7)', zIndex: 1,
            }}>
              <CircularProgress size={28} />
            </Box>
          )}
        </Box>
        <Box sx={{ p: 1.25 }}>
          <Typography variant="subtitle2" noWrap>
            {row.name || row.productName || 'No product'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {/* A named creative still says which product it is for. */}
            {row.name && row.productName ? `${row.productName} · ` : ''}
            {originLine(row, templateLabel)}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" color={STATUS_COLOR[row.status]} label={STATUS_LABEL[row.status]}
              sx={{ height: 20, fontSize: '0.65rem' }} />
            {row.mode && <Chip size="small" variant="outlined" label={row.mode} sx={{ height: 20, fontSize: '0.65rem' }} />}
            {row.parentGenerationId && (row.variantKind === 'resize' ? (
              <Tooltip title="Resize — the same creative reframed into this aspect, not a new take">
                <Chip size="small" variant="outlined" color="secondary" label="resize" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            ) : (
              <Tooltip title={row.tweak ? `Re-roll with tweak: ${row.tweak}` : 'Re-roll — same prompt and text layer, new image'}>
                <Chip size="small" variant="outlined" color="info" label="re-roll" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            ))}
            {row.similarity && (
              <Tooltip title={`${SIMILARITY_TIP[row.similarity] || ''}${!rowSourceAttached(row) && SOURCE_ATTACHED_AXES.has(row.similarity) ? ' (this run filled a layout template, so the source ad was NOT attached)' : ''}`}>
                <Chip
                  size="small"
                  variant="outlined"
                  color={rowSourceAttached(row) ? 'warning' : 'default'}
                  label={SIMILARITY_LABEL[row.similarity] || row.similarity}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Tooltip>
            )}
            {/* Structured rows are the minority; remakes get no marker of their
                own because their source thumbnail already says what they are. */}
            {isStructured(row) && (
              <Tooltip title="Built from a format, not from a source ad.">
                <Chip size="small" variant="outlined" color="primary" label="structured"
                  sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            )}
            {row.formatKey && (
              <Tooltip title={`Format: ${templateLabel(row.formatKey)} — the fixed layout this ad was built from`}>
                <Chip
                  size="small" variant="outlined" color="secondary"
                  label={templateLabel(row.formatKey)}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Tooltip>
            )}
            {/* A legacy remake that also filled a layout template. Accurate for
                those rows, and only those. */}
            {row.legacyTemplateKey && (
              <Tooltip title={`Template: ${templateLabel(row.legacyTemplateKey)} — a fixed layout this remake filled, replacing the source ad's composition`}>
                <Chip
                  size="small" variant="outlined" color="secondary"
                  label={templateLabel(row.legacyTemplateKey)}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Tooltip>
            )}
            {row.aspectRatio && <Chip size="small" variant="outlined" label={row.aspectRatio} sx={{ height: 20, fontSize: '0.65rem' }} />}
            {row.status === 'DONE' && (
              <Tooltip title={copyTip(row)}>
                <Chip
                  size="small"
                  variant={rowHasCopy(row) ? 'outlined' : 'filled'}
                  color={rowHasCopy(row) ? 'success' : 'warning'}
                  label={rowHasCopy(row) ? 'copy ✓' : 'needs copy'}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Tooltip>
            )}
            {row.finalizedAt && (
              <Tooltip title={`Finalized ${new Date(row.finalizedAt).toLocaleString()}${row.dropboxPath ? ` → ${row.dropboxPath}` : ''}`}>
                <Chip size="small" variant="outlined" color="info" label="finalized ✓" sx={{ height: 20, fontSize: '0.65rem' }} />
              </Tooltip>
            )}
            {/* Human tags last, in the Library's "this is a hand-written tag"
                styling, so they read as annotation rather than machine state. */}
            {(row.tags || []).map(tag => (
              <Chip key={tag} size="small" variant="outlined" color="secondary" label={tag}
                sx={{ height: 20, fontSize: '0.65rem' }} />
            ))}
          </Stack>
        </Box>
      </CardActionArea>
    </Card>
  );

  // A folded group. Deliberately NOT a shrunken version of the card above:
  // per-member actions (delete, source-ad inspection, the axis chip) live
  // inside the group, so this card answers only the group-level questions —
  // how many, how far along, is the shared copy written, has it shipped.
  const renderGroupCard = (unit: Extract<DisplayUnit, { kind: 'group' }>) => {
    const { groupId, rows: members, info } = unit;
    const leader = members.find(r => r.outputImageUrl) || members[0];
    const status = groupStatus(members);
    const doneMembers = members.filter(r => r.status === 'DONE');
    const allSelected = !!doneMembers.length && doneMembers.every(r => selectedRows.has(r.generationId));
    const someSelected = doneMembers.some(r => selectedRows.has(r.generationId));
    // Only claim the whole group shipped when the whole group is visible — a
    // filter can hide members, and "finalized ✓" over 2 of 6 would be a lie.
    const wholeGroupVisible = members.length === info.memberCount;
    const groupCopy = hasCopy(info.adCopy);

    return (
      <Card key={groupId} variant="outlined">
        <CardActionArea onClick={() => setOpenGroup(unit)}>
          {/* Two sheet edges peeking above the leader image: the card reads as
              a group before any chip is read. */}
          <Box aria-hidden sx={{ pt: 0.75 }}>
            <Box sx={{ height: 4, mx: 2, bgcolor: 'grey.300', borderRadius: '4px 4px 0 0' }} />
            <Box sx={{ height: 4, mx: 1, bgcolor: 'grey.400', borderRadius: '4px 4px 0 0' }} />
          </Box>
          <Box sx={{
            position: 'relative', aspectRatio: '1 / 1', bgcolor: 'grey.100',
            display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
          }}>
            {leader?.outputImageUrl ? (
              <Box component="img" src={leader.outputImageUrl} alt="Grouped creative" loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <Stack alignItems="center" spacing={1} sx={{ color: 'text.disabled' }}>
                {status.running > 0 && <CircularProgress size={22} />}
                <Typography variant="caption">{status.label}</Typography>
              </Stack>
            )}
            <Tooltip title={`${info.memberCount} creatives in this group — click to open it`}>
              <Chip size="small" color="default" label={`×${info.memberCount}`}
                sx={{
                  position: 'absolute', top: 6, right: 6, height: 20, fontSize: '0.65rem',
                  fontWeight: 600, bgcolor: 'rgba(255,255,255,0.9)',
                }} />
            </Tooltip>
            {!!doneMembers.length && (
              <Tooltip title={allSelected ? 'Deselect the whole group' : `Select all ${doneMembers.length} finished creatives in this group`}>
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onClick={e => e.stopPropagation()}
                  onChange={() => toggleGroupSelected(members)}
                  sx={{
                    position: 'absolute', top: 2, left: 2, p: 0.5,
                    bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ p: 1.25 }}>
            <Typography variant="subtitle2" noWrap>
              {info.name || leader?.productName || 'No product'}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {info.name && leader?.productName ? `${leader.productName} · ` : ''}
              {groupOriginLine(unit.rows, templateLabel)}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
              <Chip size="small" color={status.color} label={status.label}
                sx={{ height: 20, fontSize: '0.65rem' }} />
              <Tooltip title={groupCopy
                ? 'Shared headlines/captions are saved on this group'
                : 'No shared headlines or captions yet — open the group and use the Ad Copy Studio once for all of them'}>
                <Chip
                  size="small"
                  variant={groupCopy ? 'outlined' : 'filled'}
                  color={groupCopy ? 'success' : 'warning'}
                  label={groupCopy ? 'copy ✓' : 'needs copy'}
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
              </Tooltip>
              {wholeGroupVisible && members.every(r => r.finalizedAt) && (
                <Tooltip title="Every creative in this group has been published">
                  <Chip size="small" variant="outlined" color="info" label="finalized ✓" sx={{ height: 20, fontSize: '0.65rem' }} />
                </Tooltip>
              )}
              {/* The group's OWN tags. Unioning its members' in would claim the
                  group is tagged when it isn't, and would not match what the
                  filter does with a group tag either. */}
              {(info.tags || []).map(tag => (
                <Chip key={tag} size="small" variant="outlined" color="secondary" label={tag}
                  sx={{ height: 20, fontSize: '0.65rem' }} />
              ))}
            </Stack>
            {!wholeGroupVisible && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {members.length} of {info.memberCount} shown
              </Typography>
            )}
          </Box>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Box>
      <InspoSetsPanel />
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={(_e, next) => next && chooseView(next)}>
          <ToggleButton value="grid" aria-label="Card grid"><ViewModuleIcon fontSize="small" /></ToggleButton>
          <ToggleButton value="table" aria-label="Table"><ViewListIcon fontSize="small" /></ToggleButton>
        </ToggleButtonGroup>
        <TextField
          select size="small" label="Status" value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value as typeof statusFilter); setPage(0); }}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="active">Hide failed</MenuItem>
          <MenuItem value="all">Everything</MenuItem>
          <MenuItem value="DONE">Done only</MenuItem>
          <MenuItem value="FAILED">Failed only</MenuItem>
        </TextField>
        <Tooltip title={productsError ? "The product list couldn't be loaded — filtering by product is unavailable" : ''}>
          <TextField
            select size="small" label="Product" value={productFilter}
            onChange={e => { setProductFilter(e.target.value); setPage(0); }}
            disabled={productsError}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All products</MenuItem>
            {products.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.title}</MenuItem>)}
          </TextField>
        </Tooltip>
        {/* Matching a GROUP's tag keeps the whole group, matching one member's
            hides its siblings — and the existing "N of M shown" caption on the
            folded card already says which happened, so nothing here needs to. */}
        <Tooltip title={tagsError ? "Tags couldn't be loaded — filtering by tag is unavailable" : ''}>
          <Autocomplete
            multiple size="small" options={filterTagOptions} value={tagFilter}
            disabled={tagsError}
            onChange={(_e, next) => { setTagFilter(next); setPage(0); }}
            sx={{ minWidth: 220 }}
            renderInput={params => <TextField {...params} label="Tag" placeholder="Any tag" />}
          />
        </Tooltip>
        <Button
          size="small"
          disabled={!rows.some(r => r.status === 'DONE')}
          onClick={() => setSelectedRows(prev => {
            const done = rows.filter(r => r.status === 'DONE');
            const allSelected = done.every(r => prev.has(r.generationId));
            const next = new Map(prev);
            for (const row of done) {
              if (allSelected) next.delete(row.generationId);
              else next.set(row.generationId, row);
            }
            return next;
          })}
        >
          Select all
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 78 }}>
          {selectedRows.size} selected
        </Typography>
        <Tooltip title={selectedRows.size < 2
          ? 'Select two or more creatives to fold them into one group'
          : 'Fold these into one group — shared launch copy, published as one batch'}>
          <span>
            <Button
              size="small"
              disabled={selectedRows.size < 2 || grouping}
              onClick={groupSelected}
              startIcon={grouping ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              {grouping ? 'Grouping…' : 'Group'}
            </Button>
          </span>
        </Tooltip>
        {/* Exactly one, and it must still be missing a ratio. Resizing is a
            per-creative act — the reframe is driven by that image — so a
            multi-selection has no single subject and says so rather than
            silently acting on the first. */}
        {(() => {
          const only = selectedRows.size === 1 ? [...selectedRows.values()][0] : null;
          const missing = only ? missingAspects(only) : [];
          const busy = only ? sizingIds.has(only.generationId) : false;
          return (
            <Tooltip title={
              !only ? 'Select exactly one creative to reframe it into another size'
                : !missing.length ? 'This creative already has every size'
                  : addSizeHint(only)
            }>
              <span>
                <Button
                  size="small"
                  disabled={!only || !missing.length || busy}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                  onClick={e => only && setSizeMenu({ anchor: e.currentTarget, row: only })}
                >
                  {busy ? 'Queueing…' : 'Add size'}
                </Button>
              </span>
            </Tooltip>
          );
        })()}
        <Button size="small" variant="contained" disabled={!selectedRows.size} onClick={() => setFinalizeOpen(true)}>
          Finalize…
        </Button>
        <Button size="small" disabled={!selectedRows.size} onClick={() => setSelectedRows(new Map())}>
          Clear
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Stack>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&apos;t load the generations. Try Refresh — if it keeps failing the API may be down.
        </Alert>
      )}

      {/* Mutation failures land here when no dialog is open (the detail and
          re-roll dialogs each show the same state inline). */}
      {actionError && !openRow && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {/* First load: rows are empty AND loading — without this the tab is a
          blank area with a disabled toolbar until the query lands. */}
      {loading && !rows.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
          <CircularProgress />
          <Typography variant="caption" color="text.secondary">Loading generated creatives…</Typography>
        </Box>
      )}

      {!rows.length && !loading && !loadError && (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="subtitle1">Nothing generated yet</Typography>
          {/* Two ways in now, and the empty state has to name both — sending
              everyone to the Library would hide the structured half entirely. */}
          <Typography variant="body2" color="text.secondary">
            Rebuild an existing ad: open an analyzed one in the <strong>Library</strong> and hit
            Generate. Or build from a known structure: pick one in <strong>Formats</strong>.
          </Typography>
        </Paper>
      )}

      {view === 'grid' ? (
        <Box sx={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 2, opacity: loading ? 0.6 : 1,
        }}>
          {units.map(unit => (unit.kind === 'group' ? renderGroupCard(unit) : renderCard(unit.row)))}
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ overflowX: 'auto', opacity: loading ? 0.6 : 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Output</TableCell>
                <TableCell>Built from</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Aspect</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.generationId} hover onClick={() => setOpenRow(row)} sx={{ cursor: 'pointer' }}>
                  <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                    {row.status === 'DONE' && (
                      <Checkbox
                        size="small"
                        checked={selectedRows.has(row.generationId)}
                        onChange={() => toggleSelected(row)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {row.outputImageUrl
                      ? <Box component="img" src={row.outputImageUrl} alt="" loading="lazy"
                          sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 0.5, display: 'block' }} />
                      : <Typography variant="caption" color="text.disabled">{STATUS_LABEL[row.status]}</Typography>}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {row.sourceThumbnailUrl && row.staticAdId && (
                        <Box component="img" src={row.sourceThumbnailUrl} alt="" loading="lazy"
                          onClick={(e) => { e.stopPropagation(); setSourceAdId(row.staticAdId); }}
                          style={{ cursor: 'pointer' }}
                          sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 0.5 }} />
                      )}
                      <Typography variant="caption" noWrap>
                        {isStructured(row) ? originLine(row, templateLabel) : (row.advertiserName || row.pageName || '—')}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {/* The name goes here rather than in a column of its own:
                        it is what the creative is called, and most rows have
                        none, so a dedicated column would be mostly empty. */}
                    {row.name && (
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{row.name}</Typography>
                    )}
                    <Typography variant="caption" color={row.name ? 'text.secondary' : undefined}>
                      {row.productName || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption">{row.mode || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{row.aspectRatio || '—'}</Typography></TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[row.status]} label={STATUS_LABEL[row.status]}
                      sx={{ height: 20, fontSize: '0.65rem' }} />
                    {row.status === 'DONE' && (
                      <Tooltip title={copyTip(row)}>
                        <Chip
                          size="small"
                          variant={rowHasCopy(row) ? 'outlined' : 'filled'}
                          color={rowHasCopy(row) ? 'success' : 'warning'}
                          label={rowHasCopy(row) ? 'copy ✓' : 'needs copy'}
                          sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }}
                        />
                      </Tooltip>
                    )}
                    {/* The table stays FLAT — one line per generation — so a
                        grouped row is marked rather than folded away. */}
                    {row.groupId && groups[row.groupId] && (
                      <Tooltip title="Part of a group — shares launch copy and publishes as one batch. Switch to the card view to open it.">
                        <Chip size="small" variant="outlined" label={`group ×${groups[row.groupId].memberCount}`}
                          sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                      </Tooltip>
                    )}
                    {row.model === 'stub' && (
                      <Chip size="small" color="warning" label="stub" sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                    )}
                    {row.finalizedAt && (
                      <Tooltip title={`Finalized ${new Date(row.finalizedAt).toLocaleString()}${row.dropboxPath ? ` → ${row.dropboxPath}` : ''}`}>
                        <Chip size="small" variant="outlined" color="info" label="finalized ✓" sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                      </Tooltip>
                    )}
                    {/* Appended here rather than given a column — the table is
                        already wide and tags are optional annotation. */}
                    {(row.tags || []).map(tag => (
                      <Chip key={tag} size="small" variant="outlined" color="secondary" label={tag}
                        sx={{ height: 20, fontSize: '0.65rem', ml: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{row.createdAt ? new Date(row.createdAt.replace(' ', 'T')).toLocaleString() : '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {deletingIds.has(row.generationId) ? (
                      <CircularProgress size={18} />
                    ) : (
                      <IconButton size="small" onClick={e => askRemove(e, row.generationId)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* `total` and the paging window count CARDS — a group is one card and
          never splits across pages. The table view draws those cards FLAT (one
          line per member), so it says how many lines it drew rather than
          implying the card range is a row range. */}
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_e, next) => setPage(next)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        labelRowsPerPage="Cards per page:"
        labelDisplayedRows={({ from, to, count }) => (view === 'table'
          ? `${rows.length} creative${rows.length === 1 ? '' : 's'} shown — cards ${from}–${to} of ${count}`
          : `${from}–${to} of ${count}`)}
      />

      {/* ── Detail ── */}
      {/* The selection is cleared when the dialog CLOSES after a publish, not
          the moment the publish returns: clearing it mid-run re-rendered the
          dialog out from under its own results panel. A cancelled — or
          server-rejected, so nothing published — run keeps the selection. */}
      <FinalizeDialog
        open={finalizeOpen}
        onClose={({ published }) => {
          setFinalizeOpen(false);
          if (published) setSelectedRows(new Map());
        }}
        rows={[...selectedRows.values()]}
        groups={knownGroups}
        service={service}
        onFinished={() => { void load(); }}
      />

      {/* Mounted BEFORE the per-generation dialog so a member opened from the
          gallery layers ABOVE the group — closing it drops you back into the
          group rather than out to the grid. */}
      <GroupDetailDialog
        open={!!openGroup}
        unit={openGroup}
        service={service}
        onChanged={() => load()}
        onClose={() => setOpenGroup(null)}
        onOpenMember={setOpenRow}
        tagSuggestions={tagSuggestions}
        onTagAdded={() => { void loadTags(); }}
      />

      <Dialog open={!!openRow} onClose={() => setOpenRow(null)} maxWidth="lg" fullWidth>
      {openRow && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              {openRow.name || openRow.productName || 'Generation'} · {STATUS_LABEL[openRow.status]}
              {openRow.model === 'stub' && (
                <Chip size="small" color="warning" label="stub — not a real generation" sx={{ ml: 1 }} />
              )}
            </DialogTitle>
            <DialogContent dividers>
              <Box>
              {actionError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>
              )}
              {openRow.errorMessage && <Alert severity="error" sx={{ mb: 2 }}>{openRow.errorMessage}</Alert>}
              {/* This creative's own identity — its name and its OWN tags. A
                  group's name and tags are edited on the group, not here. */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <CreativeNameField
                    value={openRow.name || null}
                    onSave={async (name) => {
                      await service.saveGenerationName(openRow.generationId, name);
                      void load();
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TagEditor
                    tags={openRow.tags || []}
                    suggestions={tagSuggestions}
                    onAdd={async (tag) => {
                      const resp = await service.addGenerationTag(openRow.generationId, tag);
                      // Patch the open row so the chips move now; load() then
                      // agrees, and loadTags picks up a brand-new word.
                      setOpenRow(prev => (prev ? { ...prev, tags: resp.tags } : prev));
                      void load();
                      void loadTags();
                    }}
                    onRemove={async (tag) => {
                      const resp = await service.removeGenerationTag(openRow.generationId, tag);
                      setOpenRow(prev => (prev ? { ...prev, tags: resp.tags } : prev));
                      void load();
                    }}
                  />
                </Box>
              </Stack>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: '1 1 300px' }}>
                  <Typography variant="overline" color="text.secondary">Output</Typography>
                  {openRow.outputImageUrl ? (
                    <Box component="img" src={openRow.outputImageUrl} alt="Generated creative"
                      sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                  ) : (
                    <Typography variant="body2" color="text.disabled">No image yet.</Typography>
                  )}
                </Box>
                <Box sx={{ flex: '0 0 160px' }}>
                  {isStructured(openRow) ? (
                    <>
                      <Typography variant="overline" color="text.secondary">Format</Typography>
                      <Typography variant="body2">{templateLabel(openRow.formatKey) || '—'}</Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="overline" color="text.secondary">Source ad</Typography>
                      {openRow.sourceThumbnailUrl && openRow.staticAdId ? (
                        <Box component="img" src={openRow.sourceThumbnailUrl} alt="Source ad"
                          onClick={() => setSourceAdId(openRow.staticAdId)}
                          style={{ cursor: 'pointer' }}
                          sx={{ width: '100%', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                      ) : (
                        // The header used to sit outside the guard, so a row with
                        // no usable image rendered a titled empty column.
                        <Typography variant="body2" color="text.disabled">
                          Source image unavailable — the link from Meta may have expired.
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>

              {(openRow.outputVersions?.length || 0) > 1 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Versions ({openRow.outputVersions!.length}) — click to make one current
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {openRow.outputVersions!.map(v => (
                      <Box key={v.key} sx={{ position: 'relative', '&:hover .version-delete': { opacity: 1 }, pointerEvents: versionBusyKey ? 'none' : 'auto' }}>
                        <Tooltip title={`${v.note || 'render'}${v.at ? ` — ${new Date(v.at).toLocaleString()}` : ''}${v.current ? ' (current)' : ''}`}>
                          <Box
                            component="img"
                            src={v.url || ''}
                            alt={v.note || 'Version'}
                            onClick={async () => {
                              if (v.current || versionBusyKey) return;
                              setVersionBusyKey(v.key);
                              try {
                                await run(async () => {
                                  await service.setOutputVersion(openRow.generationId, v.key);
                                  await load();
                                });
                              } finally {
                                setVersionBusyKey(null);
                              }
                            }}
                            sx={{
                              width: 72, height: 72, objectFit: 'cover', borderRadius: 1, cursor: v.current ? 'default' : 'pointer',
                              border: '3px solid', borderColor: v.current ? 'primary.main' : 'divider',
                              opacity: v.current ? 1 : 0.75, '&:hover': { opacity: 1 }, display: 'block',
                            }}
                          />
                        </Tooltip>
                        {versionBusyKey === v.key && (
                          <Box sx={{
                            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(255,255,255,0.6)', borderRadius: 1,
                          }}>
                            <CircularProgress size={22} />
                          </Box>
                        )}
                        {!v.current && (
                          <Tooltip title="Delete this version (the current one can't be deleted)">
                            <IconButton
                              size="small"
                              className="version-delete"
                              onClick={() => setConfirmVersionKey(v.key)}
                              sx={{
                                position: 'absolute', top: -6, right: -6, p: 0.25, opacity: 0,
                                bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
                                transition: 'opacity 0.15s', '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
                              }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}>
                <Tab value="copy" label="Ad copy" sx={{ minHeight: 36 }} />
                <Tab value="inputs" label="Generation details" sx={{ minHeight: 36 }} />
                <Tab value="published" label={deliveries.length ? `Published (${deliveries.length})` : 'Published'} sx={{ minHeight: 36 }} />
              </Tabs>
              {/* Both panels stay MOUNTED (CSS-hidden) so unsaved copy edits
                  survive switching tabs. */}
              <Box sx={{ display: detailTab === 'copy' ? 'block' : 'none', pt: 1.5 }}>
                {/* A grouped member edits the GROUP's shared copy here, not its
                    own — one panel, one save, every sibling. */}
                <AdCopyPanel
                  row={openRow}
                  group={openRow.groupId ? groups[openRow.groupId] : undefined}
                  service={service}
                  onSaved={() => { void load(); }}
                  formatLabel={templateLabel(openRow.formatKey) || null}
                />
              </Box>
              <Box sx={{ display: detailTab === 'inputs' ? 'block' : 'none' }}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="overline" color="text.secondary">Attached references</Typography>
                {/* What the generator was actually shown. The single most likely
                    reason an output looks wrong is a bad reference — one that
                    was itself an ad once cloned the whole ad — so the inputs
                    belong next to the output being judged. */}
                <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                  {openRow.sourceAdAttached && openRow.sourceThumbnailUrl && (
                    <Tooltip title="Source ad image was attached to this generation">
                      <Box component="img" src={openRow.sourceThumbnailUrl} alt="Source ad reference"
                        onClick={() => setSourceAdId(openRow.staticAdId)}
                        style={{ cursor: 'pointer' }}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '2px solid', borderColor: 'warning.main' }} />
                    </Tooltip>
                  )}
                  {(openRow.attachedAssets || []).map(asset => (
                    <Tooltip key={asset.assetId} title={asset.caption || 'Product reference'}>
                      <Box component="img" src={asset.thumbnailUrl || ''} alt={asset.caption || 'Product reference'}
                        sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
                    </Tooltip>
                  ))}
                  {!openRow.sourceAdAttached && !(openRow.attachedAssets || []).length && (
                    <Typography variant="caption" color="text.disabled">
                      None — the generator worked from the prompt alone.
                    </Typography>
                  )}
                </Stack>
              </Box>

              {openRow.providerJobId && openRow.model !== 'stub' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary">Higgsfield job</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {openRow.providerJobId}
                    </Typography>
                    <Button size="small" onClick={() => { void navigator.clipboard.writeText(openRow.providerJobId || ''); }}>
                      Copy id
                    </Button>
                    {/* Higgsfield has no public per-job permalink (their /image/<id>
                        route is an SPA catch-all) — the run lives in the account's
                        asset history, newest first. */}
                    <Button size="small" href="https://higgsfield.ai/assets" target="_blank" rel="noopener noreferrer">
                      Open Higgsfield assets ↗
                    </Button>
                  </Stack>
                </Box>
              )}

              {openRow.tweak && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary">Re-roll tweak</Typography>
                  <Typography variant="body2">{openRow.tweak}</Typography>
                </Box>
              )}

              {openRow.remakeBrief && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary">Remake brief</Typography>
                  <Typography variant="body2">{openRow.remakeBrief}</Typography>
                </Box>
              )}

              {openRow.promptText && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    Prompt sent {openRow.model ? `(${openRow.model})` : ''}
                  </Typography>
                  <Box component="pre" sx={{
                    m: 0, p: 1.5, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
                    fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 260, overflowY: 'auto',
                  }}>
                    {openRow.promptText}
                  </Box>
                </Box>
              )}
              </Box>
              <Box sx={{ display: detailTab === 'published' ? 'block' : 'none', pt: 1.5 }}>
                {deliveriesError && (
                  <Alert
                    severity="error" sx={{ mb: 1 }}
                    action={(
                      <Button color="inherit" size="small" onClick={() => { if (openGenerationId) void loadDeliveries(openGenerationId); }}>
                        Retry
                      </Button>
                    )}
                  >
                    {deliveriesError}
                  </Alert>
                )}
                {!deliveries.length && !deliveriesError && (
                  <Typography variant="body2" color="text.secondary">
                    Not published yet — select it in the Output list and hit Finalize.
                  </Typography>
                )}
                <Stack spacing={1}>
                  {deliveries.map(d => (
                    <Box key={d.deliveryId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                      <Typography variant="subtitle2">{d.name}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        {new Date(d.createdAt).toLocaleString()}
                      </Typography>
                      <Stack direction="row" spacing={2}>
                        {d.dropboxUrl && (
                          <MuiLink href={d.dropboxUrl} target="_blank" rel="noopener" variant="body2">
                            Dropbox folder ↗
                          </MuiLink>
                        )}
                        {d.notionPageUrl && (
                          <MuiLink href={d.notionPageUrl} target="_blank" rel="noopener" variant="body2">
                            Notion row ↗
                          </MuiLink>
                        )}
                      </Stack>
                      {d.dropboxPath && (
                        <Typography variant="caption" color="text.secondary">{d.dropboxPath}</Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              {openRow.status === 'FAILED' && (
                <Button onClick={() => retry(openRow.generationId)}>Retry</Button>
              )}
              {openRow.outputImageUrl && (
                <Button onClick={() => downloadImage(openRow.outputImageUrl!, `creative-${openRow.generationId.slice(0, 8)}.jpg`)}>
                  Download
                </Button>
              )}
              {openRow.status === 'DONE' && openRow.model !== 'stub' && (
                <Button
                  onClick={() => {
                    setRerollTweak('');
                    setRerollPrompt(openRow.promptText || '');
                    setRerollAssets(null);
                    setRerollChosen(new Set());
                    if (openRow.productId && /^\d+$/.test(openRow.productId)) {
                      void service.listProductAssets(Number(openRow.productId)).then((resp) => {
                        const assets = resp?.assets || [];
                        setRerollAssets(assets);
                        // Preselect what the parent actually used.
                        const used = new Set((openRow.attachedAssets || []).map(a => a.assetId));
                        setRerollChosen(new Set(assets.filter(a => used.has(a.assetId)).map(a => a.assetId)));
                      });
                    }
                    setRerollOpen(true);
                  }}
                >
                  Re-roll
                </Button>
              )}
              {missingAspects(openRow).length > 0 && (
                <Tooltip title={addSizeHint(openRow)}>
                  <span>
                    <Button
                      disabled={sizingIds.has(openRow.generationId)}
                      startIcon={sizingIds.has(openRow.generationId)
                        ? <CircularProgress size={14} color="inherit" /> : undefined}
                      onClick={e => setSizeMenu({ anchor: e.currentTarget, row: openRow })}
                    >
                      {sizingIds.has(openRow.generationId) ? 'Queueing…' : 'Add size'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {openRow.status === 'DONE' && (openRow.plateImageUrl || openRow.outputImageUrl) && (() => {
                // The text layer isn't stored per version — the editor always
                // seeds from the LATEST saved spec. Editing while viewing an
                // older version would silently save over it with newer text.
                const versions = openRow.outputVersions || [];
                const onOldVersion = versions.length > 1 && !versions[versions.length - 1].current;
                return (
                  <Tooltip title={onOldVersion
                    ? "Switch to the newest version first — the text layer isn't stored per version; editing starts from the latest saved text."
                    : ''}>
                    <span>
                      <Button disabled={onOldVersion} onClick={() => setOverlayEditorOpen(true)}>Edit text</Button>
                    </span>
                  </Tooltip>
                );
              })()}
              {openRow.groupId && (
                <Tooltip title="Take this creative out of its group — it stays, with its own copy, and publishes on its own">
                  <Button
                    color="warning"
                    disabled={leavingGroup}
                    startIcon={leavingGroup ? <CircularProgress size={14} color="inherit" /> : undefined}
                    onClick={() => removeFromGroup(openRow.groupId!, openRow.generationId)}
                  >
                    {leavingGroup ? 'Removing…' : 'Remove from group'}
                  </Button>
                </Tooltip>
              )}
              <Button color="error" onClick={() => askRemove(null, openRow.generationId)}>Remove</Button>
              <Button onClick={() => setOpenRow(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

        <Dialog open={rerollOpen} onClose={rerollBusy ? undefined : () => setRerollOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Re-roll this creative</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setActionError(null)}>{actionError}</Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Generates a new image — the original stays untouched for comparison{openRow?.textMode === 'overlay'
              ? ', and the text layer (wording, positions, effects) is preserved exactly; only the image underneath changes'
              : ''}. The prompt below is exactly what was sent last time; edit it freely.
          </Typography>
          <TextField
            fullWidth multiline minRows={10} maxRows={20}
            label="Prompt"
            value={rerollPrompt}
            onChange={e => setRerollPrompt(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.5 } }}
            sx={{ mb: 1.5 }}
          />
          {rerollAssets && rerollAssets.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <ProductAssetPicker
                assets={rerollAssets}
                chosen={rerollChosen}
                onToggle={(assetId) => setRerollChosen((prev) => {
                  const next = new Set(prev);
                  if (next.has(assetId)) next.delete(assetId);
                  else next.add(assetId);
                  return next;
                })}
                caption="Reference photos for this re-roll"
              />
              {rerollChosen.size === 0 && (
                <Typography variant="caption" color="warning.main">
                  No photos selected — the product in the new image will be invented from the text description.
                </Typography>
              )}
            </Box>
          )}

          <TextField
            fullWidth multiline minRows={2} maxRows={6}
            label="Tweaks (optional)"
            placeholder="e.g. make the background warmer, have the model face left"
            value={rerollTweak}
            onChange={e => setRerollTweak(e.target.value)}
            helperText="Appended to the prompt above as explicit adjustment instructions — use for quick nudges without editing the full text. Both empty edits = pure re-roll for variety."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRerollOpen(false)} disabled={rerollBusy}>Cancel</Button>
          <Button variant="contained" onClick={submitReroll} disabled={rerollBusy}>
            {rerollBusy ? 'Queueing…' : 'Re-roll'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmVersionKey} onClose={() => setConfirmVersionKey(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete this version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            It disappears from the version strip. The current output and all other versions are
            unaffected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmVersionKey(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDeleteVersion}>Delete version</Button>
        </DialogActions>
      </Dialog>

      {/* One menu for every "add size" entry point — card corner, selection
          toolbar, detail dialog. Rendered at page level so opening it from a
          card does not tie its lifetime to that card's hover state. */}
      <Menu
        anchorEl={sizeMenu?.anchor ?? null}
        open={Boolean(sizeMenu)}
        onClose={() => setSizeMenu(null)}
      >
        {(sizeMenu ? missingAspects(sizeMenu.row) : []).map(aspect => (
          <MenuItem key={aspect} onClick={() => addSize(sizeMenu!.row, aspect)}>
            {aspectLabel(aspect)}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove this creative?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            The generated image is removed from the Output tab. The source ad and its analysis are
            not affected.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmRemove}>Remove</Button>
        </DialogActions>
      </Dialog>

      <StaticAdDetailDialog
        service={service}
        staticAdId={sourceAdId}
        onClose={() => setSourceAdId(null)}
        onChanged={() => {}}
        onOpenOther={setSourceAdId}
      />

      {openRow && (openRow.plateImageUrl || openRow.outputImageUrl) && (
        <OverlayEditor
          open={overlayEditorOpen}
          row={openRow}
          service={service}
          onClose={() => setOverlayEditorOpen(false)}
          onSaved={() => { void load(); }}
        />
      )}
    </Box>
  );
}
